/* Flooring Surgeons – product page behaviour. No dependencies.
   <fs-buy>          pack stepper on the real product-form quantity input, line total, "N packs = x m²";
                     broadcasts `fs:qty` for the sticky bar.
   <fs-sticky-atc>   sticky bar: shows after the main Add to basket scrolls away; stepper + submit proxy.
   <fs-lightbox>     full-screen gallery (old-site Fancybox look) opened from the main image.
   Suitability row   [data-suits-scroll]: › button + fade while more pills are hidden to the right.
   Gallery           ‹ › arrows over the main image (desktop: Dawn's thumbnails; phones: the swipe slider) + dots on phones. */
(() => {
  const money = (pence, currency = 'GBP') =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(pence / 100);
  const m2 = (n) => `${parseFloat(n.toFixed(3)).toLocaleString('en-GB', { maximumFractionDigits: 3 })} m²`;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  function stepInput(input, delta) {
    if (!input) return;
    const min = parseInt(input.min, 10) || 1;
    const max = parseInt(input.max, 10) || 9999;
    const current = parseInt(input.value, 10) || min;
    input.value = Math.min(max, Math.max(min, current + delta));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /* ---------------------------------------------------------------- buy card */
  if (!customElements.get('fs-buy')) {
    customElements.define(
      'fs-buy',
      class FsBuy extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          const d = this.dataset;
          this.qty = document.getElementById(d.quantityInput);
          this.form = document.getElementById(d.formId);
          this.packPrice = parseInt(d.packPrice, 10) || 0;
          this.packM2 = parseFloat(d.packM2) || 0;
          this.perM2 = parseInt(d.perM2, 10) || 0;
          this.currency = d.currency || 'GBP';
          // Trade customers (snippets/fs-trade): totals at the trade rate, ex VAT (+ the inc-VAT figure)
          this.tradeKeep = d.tradePct ? 1 - parseFloat(d.tradePct) / 100 : 0;
          this.tradeVat = parseFloat(d.tradeVat) || 1.2;
          if (!this.qty) return;

          // Always add the Full pack variant from this card (even if the URL pre-selected the sample variant).
          const idInput = this.form && this.form.querySelector('[name="id"]');
          if (idInput && d.variantId) idInput.value = d.variantId;

          this.querySelectorAll('.fs-qty [data-step]').forEach((b) =>
            b.addEventListener('click', () => stepInput(this.qty, parseInt(b.dataset.step, 10)))
          );
          this.qty.addEventListener('input', () => this.render());
          this.qty.addEventListener('change', () => this.render());
          this.qty.addEventListener('blur', () => {
            const min = parseInt(this.qty.min, 10) || 1;
            const v = parseInt(this.qty.value, 10);
            if (!v || v < min) {
              this.qty.value = min;
              this.render();
            }
          });
          this.render();
        }

        count() {
          const min = parseInt(this.qty.min, 10) || 1;
          return Math.max(min, parseInt(this.qty.value, 10) || 0);
        }

        set(sel, text) {
          const el = this.querySelector(sel);
          if (el) el.textContent = text;
          return el;
        }

        render() {
          const n = this.count();
          let total = n * this.packPrice;
          if (this.tradeKeep) {
            this.set('[data-trade-inc]', `${money(Math.round(total * this.tradeKeep), this.currency)} inc VAT`);
            total = Math.round((total * this.tradeKeep) / this.tradeVat);
          }
          this.set('[data-line-total]', money(total, this.currency));
          let summary = `Qty ${n}`;
          let note = `Qty ${n}`;
          if (this.packM2) {
            note = `${plural(n, 'pack')} = ${m2(n * this.packM2)}`;
            this.set('[data-line-note]', note);
            summary = plural(n, 'pack');
          }
          document.dispatchEvent(
            new CustomEvent('fs:qty', {
              detail: {
                n,
                summary,
                note,
                total: money(total, this.currency),
                perM2: this.packM2 ? money(this.perM2, this.currency) : '',
                unit: this.packM2 ? (n === 1 ? 'pack' : 'packs') : '',
              },
            })
          );
        }
      }
    );
  }

  /* ---------------------------------------------------------------- sticky bar */
  if (!customElements.get('fs-sticky-atc')) {
    customElements.define(
      'fs-sticky-atc',
      class FsStickyAtc extends HTMLElement {
        connectedCallback() {
          const form = document.getElementById(this.dataset.formId);
          const submit = form && form.querySelector('[type="submit"]');
          if (!submit) return;
          this.qty = document.getElementById(this.dataset.quantityInput);
          this.hidden = false;
          this.querySelector('[data-sticky-add]').addEventListener('click', () => submit.click());
          this.querySelectorAll('[data-step]').forEach((b) =>
            b.addEventListener('click', () => stepInput(this.qty, parseInt(b.dataset.step, 10)))
          );
          document.addEventListener('fs:qty', (e) => this.update(e.detail));
          document.querySelector('fs-buy')?.render?.(); // sync with a quantity set before this bar was defined
          const watch = form.closest('.fs-buy__actions') || submit;
          // The huge bottom margin makes "below the viewport" count as intersecting, so the observer also fires when
          // the page jumps straight past the button (anchor links, restored scroll) – visible only once it is above.
          new IntersectionObserver(
            (entries) => {
              const e = entries[entries.length - 1]; // entries can batch up – the last one is current
              const on = !e.isIntersecting && e.boundingClientRect.top < 0;
              this.classList.toggle('is-visible', on);
              document.body.classList.toggle('fs-sticky-on', on);
            },
            { rootMargin: '0px 0px 100000px 0px' }
          ).observe(watch);
        }

        update({ n, note, total, unit }) {
          const t = this.querySelector('[data-sticky-total]');
          if (t) t.textContent = total;
          const s = this.querySelector('[data-sticky-note]');
          if (s) s.textContent = note;
          const q = this.querySelector('[data-sticky-qty]');
          if (q) q.textContent = unit ? `${n} ${unit}` : `${n}`;
        }
      }
    );
  }

  /* ---------------------------------------------------------------- "Product Suitability" pill row */
  document.querySelectorAll('[data-suits-scroll]').forEach((wrap) => {
    const list = wrap.querySelector('ul');
    const more = wrap.querySelector('[data-suits-more]');
    if (!list || !more) return;
    const sync = () => (more.hidden = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2);
    more.addEventListener('click', () => list.scrollBy({ left: Math.max(160, list.clientWidth * 0.6), behavior: 'smooth' }));
    list.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync, { passive: true });
    new ResizeObserver(sync).observe(list);
    sync();
  });

  /* ---------------------------------------------------------------- full-screen gallery <fs-lightbox>
     Opened by Dawn's <modal-opener> (it calls document.querySelector('#ProductModal-…').show(button)).
     One image at a time in a scroll-snap track (native swipe on touch), thumbnails underneath, Esc / ← → / backdrop /
     × to close, focus trapped inside, page scroll locked, focus returned to the opener. Full-size images load only when
     their slide (or a neighbour) is shown. */
  if (!customElements.get('fs-lightbox')) {
    customElements.define(
      'fs-lightbox',
      class FsLightbox extends HTMLElement {
        connectedCallback() {
          if (this.ready) return;
          this.ready = true;
          this.dialog = this.querySelector('.fs-lb__dialog');
          this.track = this.querySelector('[data-lb-track]');
          this.slides = [...this.querySelectorAll('.fs-lb__slide')];
          this.thumbs = [...this.querySelectorAll('.fs-lb__thumb')];
          this.counter = this.querySelector('[data-lb-current]');
          this.index = 0;
          this.querySelectorAll('[data-lb-close]').forEach((b) => b.addEventListener('click', () => this.hide()));
          this.querySelectorAll('[data-lb-step]').forEach((b) =>
            b.addEventListener('click', () => this.go(this.index + parseInt(b.dataset.lbStep, 10)))
          );
          this.thumbs.forEach((b) => b.addEventListener('click', () => this.go(parseInt(b.dataset.lbGo, 10))));
          // a click on the empty stage around the picture closes, like the backdrop
          this.track.addEventListener('click', (e) => {
            if (e.target === this.track || e.target.classList.contains('fs-lb__slide')) this.hide();
          });
          // Swipes: sync the index once the scroll has settled. Scrolls we start ourselves (arrows, keys, thumbs) are
          // ignored until they reach their slide, so quick repeated presses never read a half-way position.
          let t;
          this.track.addEventListener(
            'scroll',
            () => {
              clearTimeout(t);
              t = setTimeout(() => {
                const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
                if (this.target != null && Date.now() - this.targetAt < 900) {
                  if (i === this.target) this.target = null;
                  return;
                }
                this.target = null;
                if (i !== this.index) this.select(i, false);
              }, 90);
            },
            { passive: true }
          );
          ['pointerdown', 'touchstart', 'wheel'].forEach((ev) =>
            this.track.addEventListener(ev, () => (this.target = null), { passive: true })
          );
          this.onKey = (e) => this.key(e);
        }

        load(i) {
          const img = this.slides[i]?.querySelector('img');
          if (img && img.dataset.src) {
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            img.src = img.dataset.src;
            delete img.dataset.src;
          }
        }

        select(i, scroll = true) {
          const n = this.slides.length;
          this.index = ((i % n) + n) % n;
          [this.index, this.index + 1, this.index - 1].forEach((k) => this.load(((k % n) + n) % n));
          if (this.counter) this.counter.textContent = this.index + 1;
          this.slides.forEach((s, k) => s.setAttribute('aria-hidden', k !== this.index));
          this.thumbs.forEach((b, k) => {
            const on = k === this.index;
            b.classList.toggle('is-active', on);
            if (on) {
              b.setAttribute('aria-current', 'true');
              const strip = b.closest('[data-lb-thumbs]');
              if (strip) strip.scrollTo({ left: b.parentElement.offsetLeft - (strip.clientWidth - b.offsetWidth) / 2, behavior: 'smooth' });
            } else b.removeAttribute('aria-current');
          });
          if (scroll) {
            this.target = this.index;
            this.targetAt = Date.now();
            this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: this.hidden ? 'auto' : 'smooth' });
          }
        }

        go(i) {
          this.select(i, true);
        }

        show(opener) {
          this.opener = opener || document.activeElement;
          this.querySelectorAll('.fs-lb__thumb img[data-src]').forEach((img) => {
            img.src = img.dataset.src;
            delete img.dataset.src;
          });
          const id = opener?.dataset?.mediaId || opener?.closest('[data-media-id]')?.dataset.mediaId?.split('-').pop();
          const start = Math.max(0, this.slides.findIndex((s) => s.dataset.mediaId === String(id)));
          this.hidden = false;
          this.scrollY = window.scrollY;
          document.documentElement.classList.add('fs-lb-open');
          this.target = null;
          this.select(start, false);
          this.track.scrollLeft = start * this.track.clientWidth;
          requestAnimationFrame(() => {
            this.track.scrollLeft = start * this.track.clientWidth;
            this.classList.add('is-open');
          });
          document.addEventListener('keydown', this.onKey, true);
          (this.querySelector('.fs-lb__close') || this.dialog).focus({ preventScroll: true });
        }

        hide() {
          if (this.hidden) return;
          this.classList.remove('is-open');
          this.hidden = true;
          document.documentElement.classList.remove('fs-lb-open');
          document.removeEventListener('keydown', this.onKey, true);
          if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
        }

        key(e) {
          if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            this.hide();
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.go(this.index + 1);
          } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.go(this.index - 1);
          } else if (e.key === 'Tab') {
            const f = [...this.dialog.querySelectorAll('button')].filter((b) => b.offsetParent !== null);
            if (!f.length) return;
            const first = f[0];
            const last = f[f.length - 1];
            if (!this.dialog.contains(document.activeElement)) {
              e.preventDefault();
              first.focus();
            } else if (e.shiftKey && document.activeElement === first) {
              e.preventDefault();
              last.focus();
            } else if (!e.shiftKey && document.activeElement === last) {
              e.preventDefault();
              first.focus();
            }
          }
        }
      }
    );
  }

  /* ---------------------------------------------------------------- gallery arrows + dots
     Desktop (thumbnail slider): ‹ › step through Dawn's thumbnails (one image shown at a time).
     Phones: ‹ › scroll Dawn's swipe slider; a dots tab shows the current image. No layout shift – all overlays are absolute. */
  function initGallery() {
    const desktop = window.matchMedia('(min-width: 750px)');
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (gallery.dataset.fsNav) return;
      const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
      const list = viewer && viewer.querySelector('.product__media-list');
      const slides = list ? [...list.querySelectorAll('.product__media-item')] : [];
      const thumbs = [...gallery.querySelectorAll('.thumbnail-list__item')];
      if (!viewer || slides.length < 2) return;
      gallery.dataset.fsNav = '1';
      const nav = document.createElement('div');
      nav.className = 'fs-gnav';
      nav.innerHTML =
        '<button type="button" class="fs-gnav__btn fs-gnav__btn--prev" aria-label="Previous image"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
        '<button type="button" class="fs-gnav__btn fs-gnav__btn--next" aria-label="Next image"><svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>';
      const dots = document.createElement('div');
      dots.className = 'fs-dots';
      dots.setAttribute('aria-hidden', 'true');
      slides.forEach(() => dots.appendChild(document.createElement('span')));
      viewer.append(nav, dots);
      const useThumbs = () => desktop.matches && thumbs.length > 1;
      const current = () => {
        if (useThumbs()) return Math.max(0, thumbs.findIndex((t) => t.querySelector('[aria-current="true"]')));
        return Math.round(list.scrollLeft / Math.max(1, list.clientWidth));
      };
      const render = () => {
        const i = current();
        [...dots.children].forEach((d, k) => d.classList.toggle('is-on', k === i));
      };
      const go = (delta) => {
        const n = useThumbs() ? thumbs.length : slides.length;
        const i = (current() + delta + n) % n;
        if (useThumbs()) thumbs[i].querySelector('button')?.click();
        else list.scrollTo({ left: slides[i].offsetLeft - slides[0].offsetLeft, behavior: 'smooth' });
      };
      nav.querySelector('.fs-gnav__btn--prev').addEventListener('click', () => go(-1));
      nav.querySelector('.fs-gnav__btn--next').addEventListener('click', () => go(1));
      let t;
      list.addEventListener('scroll', () => {
        cancelAnimationFrame(t);
        t = requestAnimationFrame(render);
      }, { passive: true });
      render();
    });
  }
  /* ---------------------------------------------------------------- sticky header offset
     Publishes --fs-sticky-header-h = height of whatever header is pinned to the top right now (desktop compact
     bar, or the mobile/tablet header revealed on scroll-up; 0 when none), so sticky columns and anchor jumps
     sit below it. Driven by class changes + ResizeObserver – no scroll handler, no layout thrash. */
  function initHeaderOffset() {
    const root = document.documentElement;
    const section = document.querySelector('.section-header');
    const compact = document.getElementById('FsCompactHeader');
    let last = -1;
    const measure = () => {
      let h = 0;
      if (compact && root.classList.contains('fs-compact-on')) h = compact.offsetHeight;
      else if (
        section &&
        section.classList.contains('shopify-section-header-sticky') &&
        !section.classList.contains('shopify-section-header-hidden')
      )
        h = section.offsetHeight;
      if (h !== last) {
        last = h;
        root.style.setProperty('--fs-sticky-header-h', `${h}px`);
      }
    };
    const mo = new MutationObserver(measure);
    mo.observe(root, { attributes: true, attributeFilter: ['class'] });
    if (section) mo.observe(section, { attributes: true, attributeFilter: ['class'] });
    if ('ResizeObserver' in window) {
      const ro = new ResizeObserver(measure);
      if (section) ro.observe(section);
      if (compact) ro.observe(compact);
    }
    measure();
  }

  /* ---------------------------------------------------------------- read more (description, long spec)
     Markup: [data-fs-more] > .fs-readmore__body#id + button.fs-readmore__toggle[aria-controls=id][hidden].
     Without JS everything is visible. With JS the body is clamped (CSS max-height + fade) only when it is
     actually taller than the clamp; the text always stays in the HTML. */
  function initReadMore() {
    document.querySelectorAll('[data-fs-more]').forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const body = box.querySelector('.fs-readmore__body');
      const btn = box.querySelector('.fs-readmore__toggle');
      if (!body || !btn) return;
      const more = btn.dataset.more || 'Read more';
      const less = btn.dataset.less || 'Show less';
      const check = () => {
        if (box.classList.contains('is-open')) return;
        box.classList.add('is-clamped');
        const overflowing = body.scrollHeight > body.clientHeight + 24;
        box.classList.toggle('is-clamped', overflowing);
        btn.hidden = !overflowing;
      };
      btn.addEventListener('click', () => {
        const open = !box.classList.contains('is-open');
        box.classList.toggle('is-open', open);
        box.classList.toggle('is-clamped', !open);
        btn.setAttribute('aria-expanded', open);
        btn.textContent = open ? less : more;
        if (!open) {
          const top = box.getBoundingClientRect().top;
          if (top < 0) box.scrollIntoView({ block: 'start' });
        }
      });
      check();
      let t;
      window.addEventListener('resize', () => {
        clearTimeout(t);
        t = setTimeout(check, 150);
      });
    });
  }

  /* ---------------------------------------------------------------- tab bar: highlight the band in view */
  function initTabs() {
    const links = [...document.querySelectorAll('.fs-ptabs__link')];
    const targets = links.map((a) => document.querySelector(a.getAttribute('href'))).filter(Boolean);
    if (!targets.length || !('IntersectionObserver' in window)) return;
    const seen = new Map();
    let lockUntil = 0; // after a click, keep that tab lit while the page scrolls past the other sections
    const setActive = (id) => {
      links.forEach((a) => {
        const hit = !!id && a.getAttribute('href') === `#${id}`;
        a.classList.toggle('is-active', hit);
        if (hit) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    };
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => seen.set(e.target, e.isIntersecting));
        if (Date.now() < lockUntil) return;
        const on = targets.find((t) => seen.get(t));
        if (on) setActive(on.id); // nothing in view (between bands): keep the last tab lit instead of flickering off
      },
      { rootMargin: '-30% 0px -60% 0px' }
    );
    targets.forEach((t) => io.observe(t));
    links.forEach((a) =>
      a.addEventListener('click', () => {
        setActive(a.getAttribute('href').slice(1));
        lockUntil = Date.now() + 1200;
      })
    );
    window.addEventListener('scrollend', () => (lockUntil = 0), { passive: true });
  }

  const init = () => {
    initGallery();
    initTabs();
    initHeaderOffset();
    initReadMore();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
